import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useCurrentEditor } from '@tiptap/react';

import VersionHistory from '../../../versionHistory/VersionHistory';

import { outlineTags } from '../../../outlineTags/constants';

import { selectContentOptimizerViewOnlyMode } from '../../../../pages/contentOptimizer/viewOnly/store/viewOnlySettings.selectors';
import {
    additionalContentToEditor,
    updateCurrentEditorText,
    updateOutlineEditorNewTitleContent,
} from '../../store/tiptapEditor.actions';
import {
    selectAdditionalEditorContent,
    selectCurrentEditorContent,
    selectEditorPreviewVersion,
    selectEditorVersions,
    selectOutlineEditorNewTitleContent,
    selectSaveEditorLoading,
} from '../../store/tiptapEditor.selectors';

import { getCorrectEndPosition } from '../../utils/aiSpellCheckerHelpers';
import { manageDataPrefixToContent } from '../../utils/editorHelpers';
import { insertContentAfterNode } from '../../utils/editorTransactionHelpers';
import { useIsAIContentGeneration } from '../../../../../utils/hooks/useIsAIContentGeneration';

import { nodeTypes } from '../../constants/nodeTypes';
import { copyPasteOutlineContentPrefix } from '../../constants/tiptapEditorConstants';

const classes = {
    editorVersionHistory: 'editor-version-history',
};

const EditorVersionHistory = ({
    disabled,
    editorType,
    createNewEditorVersion,
    newEditorVersionCreating,
    handleActivateEditorVersion,
    handlePreviewEditorVersion,
    handleUpdateVersionName,
    handleDeleteEditorVersion,
}) => {
    const dispatch = useDispatch();

    const { editor } = useCurrentEditor();

    const editorVersions = useSelector(selectEditorVersions(editorType));
    const currentEditorLoading = useSelector(selectSaveEditorLoading(editorType));
    const viewOnlyMode = useSelector(selectContentOptimizerViewOnlyMode);
    const editorPreviewVersion = useSelector(selectEditorPreviewVersion(editorType));
    const currentEditorContent = useSelector(selectCurrentEditorContent(editorType));
    const additionalEditorContent = useSelector(selectAdditionalEditorContent(editorType));
    const newTitleContent = useSelector(selectOutlineEditorNewTitleContent);
    const { isAIContentGeneration, isAIContentLoading } = useIsAIContentGeneration();

    const insertOutlineHeadersToEditor = useCallback(
        (titleObject = null) => {
            if (titleObject !== null) {
                const nodeHtml = `<${titleObject.tag}>${titleObject.text}</${titleObject.tag}>`;

                if (titleObject.tag === outlineTags.h1) {
                    editor.commands.insertContentAt(0, nodeHtml);
                } else {
                    const { to } = editor.state.selection;
                    const correctedPos = getCorrectEndPosition(editor, to);

                    editor.commands.insertContentAt(correctedPos, nodeHtml, {
                        updateSelection: true,
                    });
                }

                editor.commands.focus(); // Focus the editor to automatically scroll to the new content
                dispatch(updateOutlineEditorNewTitleContent(null));
            }
        },
        [dispatch, editor]
    );

    useEffect(() => {
        if (!isAIContentLoading) {
            insertOutlineHeadersToEditor(newTitleContent);
        }
    }, [isAIContentLoading, newTitleContent, insertOutlineHeadersToEditor]);

    useEffect(() => {
        if (additionalEditorContent) {
            if (editor.storage?.[nodeTypes.aiSpellChecker]?.spellCheckerVisible) {
                insertContentAfterNode(editor, nodeTypes.aiSpellChecker, additionalEditorContent);
            } else {
                editor.commands.insertContentAt(
                    0,
                    manageDataPrefixToContent(
                        false,
                        additionalEditorContent,
                        copyPasteOutlineContentPrefix
                    )
                );
                editor.commands.focus('start', { scrollIntoView: true });
            }

            dispatch(
                additionalContentToEditor({
                    editorType,
                    content: null,
                })
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalEditorContent]);

    useEffect(() => {
        const newEditorContent = editorPreviewVersion
            ? editorPreviewVersion.content
            : currentEditorContent;

        editor.commands.setContent(newEditorContent);

        // eslint-disable-next-line
    }, [editorPreviewVersion]);

    useEffect(() => {
        const updatedEditorText = editor?.getText() || '';
        if (!updatedEditorText && currentEditorContent) {
            editor.commands.setContent(currentEditorContent);
        } else {
            dispatch(
                updateCurrentEditorText({
                    editorType,
                    text: updatedEditorText,
                })
            );
        }
        // eslint-disable-next-line
    }, [currentEditorContent]);

    return (
        <div className={classes.editorVersionHistory}>
            <VersionHistory
                disabled={disabled || isAIContentGeneration}
                versions={editorVersions}
                previewVersionId={editorPreviewVersion?.id}
                loading={currentEditorLoading}
                newVersionCreating={newEditorVersionCreating}
                handleActivateVersion={handleActivateEditorVersion}
                handlePreviewVersion={handlePreviewEditorVersion}
                createNewVersion={!viewOnlyMode ? createNewEditorVersion : null}
                disableCreateNewVersionBtn={editorPreviewVersion}
                handleUpdateVersionName={!viewOnlyMode ? handleUpdateVersionName : null}
                handleDeleteVersion={!viewOnlyMode ? handleDeleteEditorVersion : null}
            />
        </div>
    );
};

export default EditorVersionHistory;
